// ============================================================
// Students API
// ============================================================

import client from './axios';
import { academicSessionsApi } from './academicSessions.api';

export const buildStudentAdmissionFormData = (payload) => {
  if (payload instanceof FormData) {
    return payload;
  }

  const formData = new FormData();

  if (payload.student) {
    formData.append('student', typeof payload.student === 'string' ? payload.student : JSON.stringify(payload.student));
  }
  if (payload.parent) {
    formData.append('parent', typeof payload.parent === 'string' ? payload.parent : JSON.stringify(payload.parent));
  }
  if (payload.address) {
    formData.append('address', typeof payload.address === 'string' ? payload.address : JSON.stringify(payload.address));
  }
  if (payload.enrollment) {
    formData.append('enrollment', typeof payload.enrollment === 'string' ? payload.enrollment : JSON.stringify(payload.enrollment));
  }
  if (payload.siblings) {
    formData.append('siblings', typeof payload.siblings === 'string' ? payload.siblings : JSON.stringify(payload.siblings));
  }
  if (payload.previous_academics) {
    formData.append('previous_academics', typeof payload.previous_academics === 'string' ? payload.previous_academics : JSON.stringify(payload.previous_academics));
  }
  if (payload.documents) {
    formData.append('documents', typeof payload.documents === 'string' ? payload.documents : JSON.stringify(payload.documents));
  }
  if (payload.photo && (payload.photo instanceof File || payload.photo instanceof Blob)) {
    formData.append('photo', payload.photo);
  } else if (payload.photoFile && (payload.photoFile instanceof File || payload.photoFile instanceof Blob)) {
    formData.append('photo', payload.photoFile);
  }
  if (Array.isArray(payload.files)) {
    payload.files.forEach((file) => {
      if (file instanceof File || file instanceof Blob) {
        formData.append('files', file);
      }
    });
  }

  return formData;
};

const resolveSessionId = async (schoolId, sessionId) => {
  let target = sessionId;
  if (target === 'undefined' || target === 'null' || target === '') {
    target = null;
  }

  if (!target && schoolId) {
    try {
      const sessions = await academicSessionsApi.getAllBySchool(schoolId);
      const list = Array.isArray(sessions) ? sessions : sessions?.data || sessions?.items || [];
      if (list.length > 0) {
        const current = list.find((s) => s.is_current) || list[0];
        target = current?.id || current?.session_id;
      }
    } catch (e) {
      console.warn('Failed to fetch academic sessions:', e);
    }
  }
  return target;
};

export const studentsApi = {
  getSiblingEnums: async () => {
    const response = await client.get('/students/sibling-enums');
    return response.data;
  },

  getClassesSections: async () => {
    const response = await client.get('/students/master/classes-sections');
    return response.data;
  },

  createAdmission: async (schoolId, payload) => {
    const formData = buildStudentAdmissionFormData(payload);
    const response = await client.post(`/students/admission?school_id=${schoolId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000,
    });
    return response.data;
  },

  getStudents: async (schoolId, page = 1, pageSize = 20, params = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('school_id', schoolId);
    queryParams.append('page', page);
    queryParams.append('page_size', pageSize);

    let hasSession = false;
    if (params.academic_session_ids && Array.isArray(params.academic_session_ids) && params.academic_session_ids.length > 0) {
      params.academic_session_ids.forEach(id => {
        if (id) queryParams.append('academic_session_ids', id);
      });
      hasSession = true;
    } else if (params.academic_session_id) {
      queryParams.append('academic_session_id', params.academic_session_id);
      hasSession = true;
    }

    if (!hasSession) {
      const targetSessionId = await resolveSessionId(schoolId, null);
      if (targetSessionId) {
        queryParams.append('academic_session_id', targetSessionId);
      }
    }

    // Append all other params dynamically
    Object.keys(params).forEach(key => {
      if (key !== 'academic_session_id' && key !== 'academic_session_ids') {
        const val = params[key];
        if (val !== undefined && val !== null && val !== '') {
          if (Array.isArray(val)) {
            val.forEach(v => {
              if (v !== undefined && v !== null && v !== '') {
                queryParams.append(key, v);
              }
            });
          } else {
            queryParams.append(key, val);
          }
        }
      }
    });

    const url = `/students?${queryParams.toString()}`;
    const response = await client.get(url);
    return response.data;
  },

  searchStudents: async (schoolId, query, filters = {}, limit = 20) => {
    const targetSessionId = await resolveSessionId(schoolId, filters.academic_session_id);

    let url = `/students/searching?school_id=${schoolId}&q=${encodeURIComponent(query)}&limit=${limit}`;
    if (targetSessionId) url += `&academic_session_id=${encodeURIComponent(targetSessionId)}`;
    if (filters.class_id) url += `&class_id=${filters.class_id}`;
    if (filters.section_id) url += `&section_id=${filters.section_id}`;

    const response = await client.get(url);
    return response.data;
  },

  getDropdown: async (schoolId, sessionId, filters = {}) => {
    const targetSessionId = await resolveSessionId(schoolId, sessionId);

    let url = `/students/dropdown?school_id=${schoolId}&academic_session_id=${encodeURIComponent(targetSessionId)}`;
    if (filters.class_id) url += `&class_id=${filters.class_id}`;
    if (filters.section_id) url += `&section_id=${filters.section_id}`;
    
    const response = await client.get(url);
    return response.data;
  },

  getDashboardStats: async (schoolId, sessionId) => {
    const targetSessionId = await resolveSessionId(schoolId, sessionId);

    if (!targetSessionId) {
      throw new Error('An Active Academic Session is required for Student Dashboard. Please create an Academic Session first.');
    }

    const url = `/students/dashboard?school_id=${encodeURIComponent(schoolId)}&academic_session_id=${encodeURIComponent(targetSessionId)}`;
    const response = await client.get(url);
    return response.data;
  },

  getById: async (schoolId, studentId) => {
    const response = await client.get(`/students/${studentId}?school_id=${schoolId}`);
    return response.data;
  },

  update: async (schoolId, studentId, payload) => {
    const formData = buildStudentAdmissionFormData(payload);
    const response = await client.put(`/students/${studentId}?school_id=${schoolId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000,
    });
    return response.data;
  },

  promote: async (schoolId, payload) => {
    const response = await client.post(`/students/promote?school_id=${schoolId}`, payload);
    return response.data;
  },

  getNextRollNumber: async (payload) => {
    const response = await client.post('/students/roll-number/next', payload);
    return response.data;
  },

  checkRollNumber: async (payload) => {
    const response = await client.post('/students/roll-number/check', payload);
    return response.data;
  },

  validateAadhaar: async (schoolId, aadhaarNo) => {
    const response = await client.post(`/students/validate-aadhaar?school_id=${schoolId}`, {
      aadhaar_no: aadhaarNo,
    });
    return response.data;
  },

  updateStatus: async (schoolId, studentId, status) => {
    const response = await client.patch(`/students/${studentId}/status?school_id=${schoolId}`, { status });
    return response.data;
  },

  bulkUpdateStatus: async (schoolId, studentIds, status) => {
    const response = await client.patch(`/students/status?school_id=${schoolId}`, {
      status,
      student_ids: studentIds,
    });
    return response.data;
  },

  downloadImportTemplate: async (schoolId) => {
    const response = await client.get(`/students/admission/import-template?school_id=${schoolId}`, {
      responseType: 'blob',
    });
    return response;
  },

  importStudents: async (schoolId, file) => {
    const formData = new FormData();
    formData.append('school_id', schoolId);
    formData.append('file', file);

    const response = await client.post('/students/admission/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });
    return response.data;
  },
};
